import React, { useState, useRef, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Grid,
    Paper,
    Chip,
    Card,
    CardContent
} from '@mui/material';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    closestCenter,
    useDroppable,
} from '@dnd-kit/core';
import {
    arrayMove,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 青春期變化數據
const teenChanges = [
    { id: 1, text: "身高和體重顯著增加", category: "both", color: "#4caf50" },
    { id: 2, text: "容易長出暗瘡", category: "both", color: "#4caf50" },
    { id: 3, text: "長出腋毛和陰毛", category: "both", color: "#4caf50" },
    { id: 4, text: "生殖器官逐漸成熟", category: "both", color: "#4caf50" },
    { id: 5, text: "臀部變得渾圓", category: "girls", color: "#e91e63" },
    { id: 6, text: "出現月經", category: "girls", color: "#e91e63" },
    { id: 7, text: "乳房隆起", category: "girls", color: "#e91e63" },
    { id: 8, text: "肌肉變得發達", category: "boys", color: "#2196f3" },
    { id: 9, text: "可能出現夢遺", category: "boys", color: "#2196f3" },
    { id: 10, text: "長出喉結，聲調變得低沉", category: "boys", color: "#2196f3" },
    { id: 11, text: "長出腿毛", category: "boys", color: "#2196f3" },
    { id: 12, text: "長出鬍鬚", category: "boys", color: "#2196f3" }
];

interface DraggableItem {
    id: number;
    text: string;
    category: string;
    color: string;
    isPlaced: boolean;
    placedIn: string | null;
}

interface CircleInfo {
    x: number;
    y: number;
    radius: number;
    category: string;
}

// 可拖拽的項目組件
function DraggableItemComponent({ item }: { item: DraggableItem }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: item.id,
        disabled: item.isPlaced // 已放置的項目不能拖拽
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : (item.isPlaced ? 1 : 10), // 拖拽中的項目 z-index 最高
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            {...(item.isPlaced ? {} : attributes)}
            {...(item.isPlaced ? {} : listeners)}
            sx={{
                p: 1,
                backgroundColor: item.isPlaced ? item.color : 'white', // 配對前是白色背景，配對後顯示正確顏色
                color: item.isPlaced ? 'white' : 'black', // 配對前黑色文字，配對後白色文字
                border: `2px solid ${item.isPlaced ? item.color : '#000000'}`, // 配對前黑色邊框，配對後顯示正確顏色
                borderRadius: 2,
                cursor: item.isPlaced ? 'default' : 'grab',
                userSelect: 'none',
                minWidth: 120,
                maxWidth: 120,
                textAlign: 'center',
                opacity: item.isPlaced ? 0.9 : 1,
                boxShadow: item.isPlaced ? 2 : 1,
                position: 'relative',
                // 觸控友好的樣式
                touchAction: 'none', // 防止觸控滾動干擾
                WebkitTouchCallout: 'none', // 防止iOS長按選單
                WebkitUserSelect: 'none', // 防止iOS文字選擇
                '&:active': {
                    cursor: item.isPlaced ? 'default' : 'grabbing',
                    transform: item.isPlaced ? 'none' : 'scale(1.1)', // 觸控時放大更多
                },
                '&:hover': {
                    transform: item.isPlaced ? 'none' : 'scale(1.05)',
                    boxShadow: item.isPlaced ? 2 : 3,
                },
                transition: 'all 0.2s ease',
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.8rem' }}>
                {item.text}
            </Typography>
            {item.isPlaced && (
                <Typography variant="caption" sx={{ 
                    display: 'block', 
                    color: 'white', 
                    opacity: 0.8,
                    fontSize: '0.6rem',
                    mt: 0.5
                }}>
                    ✓ 已配對
                </Typography>
            )}
        </Paper>
    );
}

// 放置區域組件
function DropZone({ 
    id, 
    title, 
    color, 
    items,
    activeId
}: { 
    id: string; 
    title: string; 
    color: string; 
    items: DraggableItem[];
    activeId: number | null;
}) {
    const { isOver, setNodeRef } = useDroppable({
        id: id,
    });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                minHeight: 320, // 增加高度
                border: `3px dashed ${isOver ? color : '#ccc'}`,
                borderRadius: 2,
                p: 3, // 增加內邊距
                m: 1, // 增加外邊距來擴大檢測範圍
                backgroundColor: isOver ? `${color}20` : '#fafafa',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 5, // 放置區域的 z-index
                // 當有項目正在拖拽時，增強視覺效果
                transform: activeId !== null ? 'scale(1.02)' : 'scale(1)',
                boxShadow: activeId !== null ? `0 4px 20px ${color}40` : '0 2px 8px rgba(0,0,0,0.1)',
                // 觸控友好的樣式
                touchAction: 'none', // 防止觸控滾動干擾
                WebkitTouchCallout: 'none', // 防止iOS長按選單
                // 添加一個隱形的擴展區域
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -15,
                    left: -15,
                    right: -15,
                    bottom: -15,
                    border: '2px solid transparent',
                    borderRadius: 2,
                    pointerEvents: 'auto',
                    zIndex: -1,
                }
            }}
        >
            <Typography variant="h6" sx={{ color, fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                {title}
            </Typography>
            
            {/* 項目計數 */}
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
                已放置: {items.length} 個項目
            </Typography>
            
            {/* 項目排列區域 */}
            <Box sx={{ 
                minHeight: 220, // 增加高度以容納更多項目
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
                justifyContent: items.length > 0 ? 'flex-start' : 'center',
                position: 'relative',
                zIndex: 2, // 排列區域的 z-index 較低
                // 添加一些內邊距確保項目不會貼邊
                py: 1,
            }}>
                {items.length > 0 ? (
                    items.map((item, index) => (
                        <Box
                            key={item.id}
                            sx={{
                                animation: `slideIn 0.3s ease ${index * 0.1}s both`,
                                position: 'relative',
                                zIndex: 1, // 已放置項目的 z-index 最低
                                // 當有項目正在拖拽時，降低已放置項目的透明度
                                opacity: activeId !== null ? 0.6 : 1,
                                transition: 'opacity 0.2s ease',
                                '@keyframes slideIn': {
                                    '0%': {
                                        opacity: 0,
                                        transform: 'translateY(-20px)',
                                    },
                                    '100%': {
                                        opacity: 1,
                                        transform: 'translateY(0)',
                                    },
                                },
                            }}
                        >
                            <DraggableItemComponent item={item} />
                        </Box>
                    ))
                ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        拖拽項目到這裡
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export function PageGSQuizDnd() {
    const [items, setItems] = useState<DraggableItem[]>(() => 
        teenChanges.map((item) => ({
            ...item,
            isPlaced: false,
            placedIn: null
        }))
    );
    
    const [score, setScore] = useState(0);
    const [gameComplete, setGameComplete] = useState(false);
    const [activeId, setActiveId] = useState<number | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200, // 觸控延遲，避免與滾動衝突
                tolerance: 5, // 觸控容差
            },
        })
    );

    // 獲取各區域的項目
    const boysItems = items.filter(item => item.placedIn === 'boys');
    const girlsItems = items.filter(item => item.placedIn === 'girls');
    const bothItems = items.filter(item => item.placedIn === 'both');
    const unplacedItems = items.filter(item => !item.isPlaced);

    const handleDragStart = (event: DragStartEvent) => {
        console.log('🚀 Drag started:', event.active.id);
        setActiveId(event.active.id as number);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        console.log('🏁 Drag end event:', { active: active.id, over: over?.id });

        if (!over) {
            console.log('❌ No drop target found');
            return;
        }

        const itemId = active.id as number;
        const zoneId = over.id as string;
        
        console.log('📍 Drag end:', { itemId, zoneId });

        // 檢查配對是否正確
        const item = items.find(i => i.id === itemId);
        if (!item) {
            console.log('❌ Item not found');
            return;
        }

        let isCorrect = false;
        let newPlacedIn: string | null = null;

        if (zoneId === 'boys' && item.category === 'boys') {
            isCorrect = true;
            newPlacedIn = 'boys';
        } else if (zoneId === 'girls' && item.category === 'girls') {
            isCorrect = true;
            newPlacedIn = 'girls';
        } else if (zoneId === 'both' && item.category === 'both') {
            isCorrect = true;
            newPlacedIn = 'both';
        }

        console.log('🎯 Pairing result:', { isCorrect, newPlacedIn, itemCategory: item.category, zoneId });

        if (isCorrect) {
            console.log('✅ Correct pairing!');
            setItems(prev => prev.map(i => 
                i.id === itemId 
                    ? { ...i, isPlaced: true, placedIn: newPlacedIn }
                    : i
            ));
            setScore(prev => prev + 1);
            
            // 顯示成功提示
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } else {
            console.log('❌ Incorrect pairing');
            // 顯示錯誤提示
            setShowError(true);
            setTimeout(() => setShowError(false), 2000);
        }

        // 檢查遊戲是否完成
        const newPlacedCount = items.filter(i => i.isPlaced).length + (isCorrect ? 1 : 0);
        if (newPlacedCount === teenChanges.length) {
            setGameComplete(true);
        }
    };

    const handleRestart = () => {
        setItems(teenChanges.map((item) => ({
            ...item,
            isPlaced: false,
            placedIn: null
        })));
        setScore(0);
        setGameComplete(false);
        setShowSuccess(false);
        setShowError(false);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom 
                    sx={{ 
                        mb: 2,
                        background: 'linear-gradient(45deg, #2196f3, #e91e63, #4caf50)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 'bold',
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        fontSize: { xs: '2rem', md: '3rem' }
                    }}
                >
                    🧬 青春期變化配對遊戲
                </Typography>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        opacity: 0.8,
                        fontWeight: 300
                    }}
                >
                    探索成長的奧秘 ✨
                </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        mb: 2,
                        color: 'primary.main',
                        fontWeight: 500,
                        fontSize: { xs: '1rem', md: '1.25rem' }
                    }}
                >
                    🎯 將下方的青春期變化特徵拖拽到正確的區域內！
                </Typography>
                
                {/* 遊戲說明 */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2
                }}>
                    <Chip 
                        label="💡 配對前為黑色邊框" 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip 
                        label="📱 支持觸控拖拽" 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip 
                        label="✨ 拖拽時項目變透明" 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                    />
                </Box>
            </Box>

            {/* 分數顯示和狀態提示 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Chip 
                    label={`得分: ${score} / ${teenChanges.length}`} 
                    color="primary" 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', px: 2, py: 1 }}
                />
                {showSuccess && (
                    <Chip 
                        label="✅ 配對成功！" 
                        color="success" 
                        sx={{ fontSize: '1rem', px: 2, py: 1, animation: 'pulse 0.5s ease-in-out' }}
                    />
                )}
                {showError && (
                    <Chip 
                        label="❌ 配對錯誤，請重試" 
                        color="error" 
                        sx={{ fontSize: '1rem', px: 2, py: 1, animation: 'shake 0.5s ease-in-out' }}
                    />
                )}
            </Box>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                {/* 未放置的項目 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        拖拽項目：
                    </Typography>
                    <Grid container spacing={2}>
                        {unplacedItems.map((item) => (
                            <Grid item key={item.id}>
                                <DraggableItemComponent item={item} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 放置區域 */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <DropZone
                            id="boys"
                            title="男孩的變化"
                            color="#2196f3"
                            items={boysItems}
                            activeId={activeId}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DropZone
                            id="both"
                            title="共同變化"
                            color="#4caf50"
                            items={bothItems}
                            activeId={activeId}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DropZone
                            id="girls"
                            title="女孩的變化"
                            color="#e91e63"
                            items={girlsItems}
                            activeId={activeId}
                        />
                    </Grid>
                </Grid>

                <DragOverlay>
                    {activeId ? (
                        <DraggableItemComponent item={items.find(item => item.id === activeId)!} />
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* 控制按鈕 */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleRestart}
                    size="large"
                    sx={{ minWidth: 150 }}
                >
                    {gameComplete ? '再玩一次' : '重新開始'}
                </Button>
            </Box>

            {/* 完成遊戲訊息 */}
            {gameComplete && (
                <Card sx={{ mt: 3, backgroundColor: '#e8f5e8' }}>
                    <CardContent>
                        <Typography variant="h5" align="center" gutterBottom>
                            🎉 恭喜你完成遊戲！
                        </Typography>
                        <Typography variant="body1" align="center">
                            你成功將所有青春期變化特徵配對到正確的位置！
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
